import { useEffect, useMemo, useState } from 'react';

import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute, useParams } from '@tanstack/react-router';

import heartFloatingLottie from 'src/assets/animations/HeartFloatingEmoji.lottie?url';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import ThanksDisplay from 'src/components/ThanksDisplay';
import VisitSummaryDisplay from 'src/components/VisitSummaryDisplay';
import { TeamRoles } from 'src/constants/TeamRoles';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useCreateVisitThankMutation, useGetVisitByRidQuery } from 'src/store/sageHomeApi';
import { isActor } from 'src/utils/personUtils';

export const Route = createFileRoute('/(app)/visits/summary/detail/$visitRid')({
    component: VisitSummaryComponent,
});

const PageName = 'Visit Summary Detail';

function VisitSummaryComponent() {
    const { visitRid } = useParams({
        from: '/(app)/visits/summary/detail/$visitRid',
    });
    const { palette, spacing } = useTheme();

    const { trackPageView } = useAnalytics();
    const { showError, showSuccess } = useSnackbar();
    const { isMobile } = useResponsive();
    const { careRecipient, currentUser } = useTeamMemberData();
    const careRecipientRid = careRecipient?.teamMemberRid;
    const isLicensedCaregiver = currentUser?.role === TeamRoles.LicensedCaregiver;

    const [shouldShowSentMessage, setShouldShowSentMessage] = useState(false);

    const {
        data: currentVisit,
        isLoading,
        error,
    } = useGetVisitByRidQuery(
        {
            careRecipientRid: careRecipientRid || '',
            visitRid: visitRid || '',
        },
        { skip: !visitRid || !careRecipientRid }
    );

    const caregiverRid = currentVisit?.caregiverRid;

    const currentUserHasThanked = currentVisit?.thanks?.some((thank) =>
        isActor(thank.thankGiverRid, currentUser)
    );

    const currentUserIsVisitCaregiver = isActor(caregiverRid ?? '', currentUser);

    const [createVisitThank, { isLoading: isCreatingThank }] = useCreateVisitThankMutation();

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Show the sent message for 3 seconds
        if (shouldShowSentMessage) {
            const timeout = setTimeout(() => {
                setShouldShowSentMessage(false);
            }, 3000);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [shouldShowSentMessage]);

    const handleSayThankYou = async () => {
        if (!careRecipientRid || !visitRid || !caregiverRid) {
            return;
        }

        try {
            await createVisitThank({
                careRecipientRid: careRecipientRid,
                visitRid,
                createVisitThankRequest: {
                    thankReceiverRid: caregiverRid,
                },
            }).unwrap();
            setShouldShowSentMessage(true);
            showSuccess('Thanks sent to the caregiver.');
        } catch (error) {
            showError('An error occurred, please try again.');
            console.error('Failed to create visit thank:', error);
        }
    };

    const thanksCount = currentVisit?.thanks?.length || 0;

    // Determine footer visibility and content state
    const shouldShowFooter = !isLicensedCaregiver || thanksCount > 0;
    const shouldShowThankCount = (!isLoading && thanksCount > 0) || currentUser?.isTeamAdmin;

    // Determine what to show in the main action area
    const footerContentState = useMemo(() => {
        if (shouldShowSentMessage) {
            return 'sent-message';
        }
        if (!currentUserIsVisitCaregiver && !isLicensedCaregiver && !currentUserHasThanked) {
            return 'thank-button';
        }
        if (shouldShowThankCount && !shouldShowSentMessage) {
            return 'thanks-display';
        }
        return null;
    }, [
        shouldShowSentMessage,
        currentUserIsVisitCaregiver,
        isLicensedCaregiver,
        currentUserHasThanked,
        shouldShowThankCount,
    ]);

    return (
        <InnerMaxWidthContainer>
            <PageHeader />
            <Stack sx={{ gap: 4 }}>
                {isLoading ? (
                    <Typography variant="body1">Loading...</Typography>
                ) : error ? (
                    <Typography color="error" variant="body1">
                        There was an error loading the visit. Please try again.
                    </Typography>
                ) : currentVisit?.visitSummary ? (
                    <Stack sx={{ gap: 2 }}>
                        <Typography variant="h1" sx={{ fontWeight: 600 }}>
                            Care Visit Notes
                        </Typography>
                        <VisitSummaryDisplay
                            visitSummary={currentVisit.visitSummary}
                            caregiverRid={caregiverRid}
                        />
                    </Stack>
                ) : (
                    <Typography variant="body1">No visit summary available.</Typography>
                )}

                {shouldShowFooter && (
                    <PageFooter includeShadow={isMobile}>
                        <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                            {/* Thanks count indicator */}
                            {shouldShowThankCount && (
                                <Stack
                                    direction="row"
                                    sx={{ gap: 0.5, alignItems: 'center', position: 'relative' }}
                                >
                                    <FontAwesomeIcon
                                        icon={faHeart}
                                        size="lg"
                                        color={palette.red?.[800]}
                                    />
                                    <Typography variant="body1" fontWeight={600}>
                                        {thanksCount}
                                    </Typography>

                                    {shouldShowSentMessage && (
                                        <DotLottieReact
                                            src={heartFloatingLottie}
                                            autoplay
                                            style={{
                                                position: 'absolute',
                                                top: '-300%',
                                                left: '-20%',
                                                transform: 'scale(7)',
                                            }}
                                        />
                                    )}
                                </Stack>
                            )}

                            {/* Main action area */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    flex: 1,
                                    // TODO: find a better way of doing this
                                    minHeight:
                                        isLicensedCaregiver ||
                                        currentUserHasThanked ||
                                        currentUserIsVisitCaregiver
                                            ? 'auto'
                                            : spacing(7.5),
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {footerContentState === 'thank-button' && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={handleSayThankYou}
                                        disabled={isCreatingThank || !caregiverRid}
                                    >
                                        Say Thank You
                                    </Button>
                                )}

                                {currentVisit && footerContentState === 'thanks-display' && (
                                    <ThanksDisplay visit={currentVisit} textOnly />
                                )}

                                {footerContentState === 'sent-message' && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            Thanks sent to the caregiver.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </PageFooter>
                )}
            </Stack>
        </InnerMaxWidthContainer>
    );
}
